import { useEffect, useMemo, useRef, useState } from "react";
import { getMapLayer, getMapDefinitions } from "@/api/objects";

const OBJECTS_NOT_FOUND_MESSAGE_KEY = "map-objects-not-found";

export const useMapObjects = (message, filters, bbox, zoom) => {
  const [foundObjects, setFoundObjects] = useState([]);
  const [viewportObjects, setViewportObjects] = useState([]);
  const [viewportClusters, setViewportClusters] = useState([]);
  const [mode, setMode] = useState("objects");
  const [bboxFromSearch, setBboxFromSearch] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [objectsTypes, setObjectsTypes] = useState([]);
  const [isObjectTypesReady, setIsObjectTypesReady] = useState(false);
  const isObjectsNotFoundMessageVisibleRef = useRef(false);

  const search = filters?.search;
  const definitionUuid = filters?.definition_uuid;

  const normalizedSearch = typeof search === "string" ? search.trim() : search;

  const hasFilters = Boolean(filters);
  const hasSearch = Boolean(normalizedSearch);
  const hasDefinitions =
    Array.isArray(definitionUuid) && definitionUuid.length > 0;
  const hasActiveFilters = hasSearch || hasDefinitions;

  useEffect(() => {
    const fetchObjectsTypes = async () => {
      try {
        const res = await getMapDefinitions();

        setObjectsTypes(res.data ?? res);
      } catch (e) {
        console.error(e);
      } finally {
        setIsObjectTypesReady(true);
      }
    };

    fetchObjectsTypes();
  }, []);

  useEffect(() => {
    if (!hasFilters) {
      setFoundObjects([]);
      setViewportObjects([]);
      setViewportClusters([]);
      setBboxFromSearch(null);
      setIsDataLoading(false);
      return;
    }

    if (!isObjectTypesReady) return;

    if (typeof zoom !== "number") return;

    if (hasDefinitions) {
      const objectTypeUuids = new Set(
        objectsTypes.map((definition) => definition.uuid),
      );
      const hasOnlyKnownDefinitions = definitionUuid.every((uuid) =>
        objectTypeUuids.has(uuid),
      );

      if (!hasOnlyKnownDefinitions) {
        setFoundObjects([]);
        setViewportObjects([]);
        setViewportClusters([]);
        setBboxFromSearch(null);
        setIsDataLoading(false);
        return;
      }
    }

    if (!hasActiveFilters && !bbox) return;

    let isCancelled = false;

    const fetchViewport = async () => {
      setIsDataLoading(true);

      try {
        const requestParams = {
          zoom,
        };

        if (!hasActiveFilters) {
          requestParams.bbox = JSON.stringify(bbox);
        }

        if (hasSearch) {
          requestParams.search = search;
        }

        if (hasDefinitions) {
          requestParams.definitions_uuids = definitionUuid;
        }

        const res = await getMapLayer(requestParams);

        if (isCancelled) return;

        const responseMode = res.viewMode ?? "objects";
        const objects = res.objects ?? [];
        const clusters = res.clusters ?? [];

        setMode(responseMode);
        setViewportObjects(objects);
        setViewportClusters(clusters);

        if (hasActiveFilters) {
          setFoundObjects(objects);
          setBboxFromSearch(res.bbox ?? null);

          if (
            !objects.length &&
            message &&
            !isObjectsNotFoundMessageVisibleRef.current
          ) {
            isObjectsNotFoundMessageVisibleRef.current = true;

            message.open({
              type: "warning",
              content: "Объекты не найдены",
              key: OBJECTS_NOT_FOUND_MESSAGE_KEY,
              onClose: () => {
                isObjectsNotFoundMessageVisibleRef.current = false;
              },
            });
          }
        } else {
          setFoundObjects([]);
          setBboxFromSearch(null);
        }
      } catch (error) {
        if (!isCancelled) {
          message?.open({
            type: "error",
            content: error.message,
            duration: 10,
          });
        }
      } finally {
        if (!isCancelled) {
          setIsDataLoading(false);
        }
      }
    };

    fetchViewport();

    return () => {
      isCancelled = true;
    };
  }, [
    bbox,
    hasFilters,
    isObjectTypesReady,
    objectsTypes,
    zoom,
    search,
    hasSearch,
    definitionUuid,
    hasDefinitions,
    hasActiveFilters,
    message,
  ]);

  const mergedData = useMemo(() => {
    if (!hasActiveFilters) return viewportObjects;

    const map = new Map();

    [...viewportObjects, ...foundObjects].forEach((item) => {
      if (item?.object?.uuid) {
        map.set(item.object.uuid, item);
      }
    });

    return Array.from(map.values());
  }, [foundObjects, viewportObjects, hasActiveFilters]);

  return {
    data: mergedData,
    clusters: viewportClusters,
    mode,
    foundObjects,
    isDataLoading,
    bboxFromSearch,
    objectsTypes,
    isObjectTypesReady,
    // refetch: fetchData,
  };
};

export default useMapObjects;
